import { CodeSnippet, EmptyState, Link } from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

const useStyles = makeStyles()(theme => ({
  code: {
    borderRadius: 6,
    margin: theme.spacing(2, 0),
    background:
      theme.palette.mode === 'dark' ? '#444' : theme.palette.common.white,
  },
}));

function generateYamlExample(
  annotations: string[],
  entity?: Entity,
): { yamlText: string; lineNumbers: number[] } {
  const kind = entity?.kind ?? 'Component';
  const name = entity?.metadata.name ?? 'example';
  const type = (entity?.spec?.type as string) ?? 'website';
  const owner = (entity?.spec?.owner as string) ?? 'user:default/guest';

  const yamlText = `apiVersion: backstage.io/v1alpha1
kind: ${kind}
metadata:
  name: ${name}
  annotations:${annotations.map(ann => `\n    ${ann}: value`).join('')}
spec:
  type: ${type}
  owner: ${owner}`;

  let line = 6; // Line 6 is the line number that annotations are added to.
  const lineNumbers: number[] = [];
  annotations.forEach(() => {
    lineNumbers.push(line);
    line++;
  });

  return {
    yamlText,
    lineNumbers,
  };
}

function generateDescription(annotations: string[], entityKind = 'Component') {
  const isSingular = annotations.length <= 1;
  return (
    <>
      The {isSingular ? 'annotation' : 'annotations'}{' '}
      {annotations
        .map((ann, index) => <code key={`${ann}-${index}`}>{ann}</code>)
        .reduce((prev, curr) => (
          <>
            {prev}, {curr}
          </>
        ))}{' '}
      {isSingular ? 'is' : 'are'} missing. You need to add the{' '}
      {isSingular ? 'annotation' : 'annotations'} to your {entityKind} if you
      want to enable this tool.
    </>
  );
}

type Props = {
  annotation: string | string[];
  readMoreUrl?: string;
};

/**
 * @public
 * Renders an empty state when an annotation is missing from an entity.
 */
export function CardMissingAnnotationEmptyState(props: Readonly<Props>) {
  const entityContext = useEntity();
  const { classes } = useStyles();

  const { annotation, readMoreUrl } = props;
  const annotations = Array.isArray(annotation) ? annotation : [annotation];
  const url =
    readMoreUrl ??
    'https://odp.i.mercedes-benz.com/docs/default/component/odp-user-doc/catalog/catalog/';

  const entity = entityContext.entity;
  const entityKind = entity?.kind ?? 'Component';
  const { yamlText, lineNumbers } = generateYamlExample(annotations, entity);
  return (
    <EmptyState
      missing={{ customImage: <></> }}
      title="Missing Annotation"
      description={generateDescription(annotations, entityKind)}
      action={
        <>
          <Typography variant="body1">
            Add the annotation to your {entityKind} YAML as shown in the
            highlighted example below:
          </Typography>
          <Box className={classes.code}>
            <CodeSnippet
              text={yamlText}
              language="yaml"
              showLineNumbers
              highlightedNumbers={lineNumbers}
              customStyle={{ background: 'inherit', fontSize: '115%' }}
            />
          </Box>
          <Button color="primary" component={Link} to={url}>
            Read more
          </Button>
        </>
      }
    />
  );
}
